/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import { Card, Container, Row, Col, Progress, Button, Input } from "reactstrap";

// Calendar library
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import styles from '../../assets/css/Calendar.module.scss';
import Api from "../../components/helpers/Api";
import withNavigationHook from "../../hooks/withNavigationHook";
import {RiEditFill} from "react-icons/ri";
import { debugLog } from "../../utils/debug";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import {SeoHelper} from "../../components/helpers/SeoHelper";

class LectionaryDates extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      month: new Date(),
      entries: [],
      selectedDate: null,
      activeButtonActive: false,
      seasonList: [],
      selectedSeason: ''
    };

    this.handleMonthChange = this.handleMonthChange.bind(this);
    this.handleDayClick = this.handleDayClick.bind(this);
    // this.handleSeasonChange = this.handleSeasonChange.bind(this);

    debugLog("Clearing previously curated songs");
    // sessionStorage.removeItem("curated-songs");
    // sessionStorage.removeItem("playlist");
    sessionStorage.removeItem("selected-themes");
    sessionStorage.removeItem("selected-passages");
  }


    componentDidMount() {
        this.loadLectionaryDates();
    }

    loadLectionaryDates = () => {
        const selectedLectionary = sessionStorage.getItem("selected-lectionary");

        Api.get('/api/lectionary')
            .then(response => {
                const lectionaries = response.data;
                const lectionary = lectionaries.find(l => l.lectionary_name === selectedLectionary);
                this.setState({lectionary: lectionary});

                if (lectionary) {
                    const lectionaryId = lectionary.lectionary_id;

                    Api.get(`/api/lectionary/season?lectionaryId=${lectionaryId}`)
                        .then(response => {
                            this.setState({ seasonList: response.data });
                        })
                        .catch(error => {
                            console.error('Error fetching seasons:', error);
                        });

                    Api.get(`/api/lectionary/season/dates?lectionaryId=${lectionaryId}`)
                        .then(response => {
                            this.setState({ entries: response.data });
                        })
                        .catch(error => {
                            console.error('Error fetching season dates:', error);
                        });
                } else {
                    console.error(sessionStorage.getItem("selected-lectionary") + ' Lectionary not found');
                }
            })
            .catch(error => {
                console.error('Error fetching lectionaries:', error);
            });
    }


    handleMonthChange(month) {
    this.setState({ month });
  }

  formatUKDate = (date) =>
      date.toLocaleDateString('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).split('/').reverse().join('-');

  handleDayClick = (day, selected, modifiers) => {
    const dateStr = day.toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).split('/').reverse().join('-');

    const isValid = this.state.entries.some(e => e.date === dateStr);

    if (isValid) {
      this.setState({ selectedDate: day });
      sessionStorage.setItem("selected-date", dateStr);

        const selectedDate = new Date(sessionStorage.getItem("selected-date"))
            .toISOString().split('T')[0]; // Format: 'YYYY-MM-DD'
        Api.get(`/api/lectionary/season/name?date=${selectedDate}&lectionaryId=${this.state.lectionary.lectionary_id}`)
            .then(response => {
                this.setState({ seasonName: response.data[0] });
            })
            .catch(error => {
                console.error('Error fetching seasons:', error);
            });

        return;
    }

    this.setState({ selectedDate: null });
    sessionStorage.removeItem("selected-date");
  };

  continueButtonAvailable = () => {
    sessionStorage.setItem(
        "songBooksContinueEnabled",
        sessionStorage.getItem("selected-date") &&
        JSON.parse(sessionStorage.getItem("selected-date")).length > 0
    );
  }

  renderDay = (day) => {
    return (
        <div style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '0.9rem',
        }}>
          {day.getDate()}
        </div>
    );
  };

  isContinueButtonEnabled = () => {
    return !sessionStorage.getItem("selected-date");
  }

  clearDateHandler = () => {
    sessionStorage.removeItem("selected-date");

    // Reset selectedDate in state to trigger re-render
    this.setState({ selectedDate: undefined }, () => {
      // Optional: force DayPicker to update visually
      const selectedButtons = document.querySelectorAll('.rdp-day_selected, .rdp-selected');
      selectedButtons.forEach(btn => btn.classList.remove('rdp-day_selected', 'rdp-selected'));
    });
  };

  render() {
    const selectedDateStr = sessionStorage.getItem("selected-date");
    const readingDate = selectedDateStr
        ? new Date(selectedDateStr).toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "Europe/London"
        })
        : null;

      const seasonName = this.state.seasonName ? this.state.seasonName : null;

      const choosenDateMessage = !sessionStorage.getItem("selected-date") ?
      <>
        <h5 className="mb-0">Choose a date with a reading</h5>
      </> :
      <>
        <h5 className="mb-0">Chosen date: <strong>{seasonName}</strong> ({readingDate})</h5>&nbsp;&nbsp;&nbsp;
          <Button
            type="button"
            id="clearDateButton"
            className="btn-neutral"
            onClick={this.clearDateHandler}
        >
          Clear date
        </Button>
      </>;

    const { seasonList, selectedSeason } = this.state;

      const raw = sessionStorage.getItem("selected-lectionary") || "";
      const lower = raw.toLowerCase();

      const display = lower.includes("lectionary")
          ? lower                     // It already has "lectionary" somewhere → don't add
          : `${lower} lectionary`;    // It doesn't → add it

      return (
        <>
          <SeoHelper
              uri={"/lectionary/" + sessionStorage.getItem("selected-lectionary")}
              title = "Choose a Lectionary Date | The Worship Bible"
              description={"Build song-flows using the " + sessionStorage.getItem("selected-lectionary") + " lectionary. The Worship Bible helps you create meaningful song-flows rooted in biblical themes and teachings."}
          />

          <div className="position-relative">
            <section className="section section-lg section-shaped pb-10">
              <div className="shape shape-style-1 shape-default">
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows{" "}
                        <span>from the {display}</span>
                      </h1>
                      <p className="lead text-white">
                        &nbsp;<br/>&nbsp;
                      </p>
                    </Col>
                  </Row>
                </div>
              </Container>
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon
                      className="fill-white"
                      points="2560 0 2560 100 0 100"
                  />
                </svg>
              </div>
            </section>
          </div>

          <section className="section section-lg pt-lg-9">
            <Container>
              <Card className="card-profile shadow mt--300">
                <div className="px-4 mt-3">
                  {/*<Row className="justify-content-center">*/}
                  {/*  <Col lg="6" className="text-center mb-4">*/}
                  {/*    <select*/}
                  {/*        id="lectionaySeason"*/}
                  {/*        className="custom-select"*/}
                  {/*        style={{width: "100%"}}*/}
                  {/*        onChange={this.handleSeasonChange}*/}
                  {/*        value={selectedSeason}*/}
                  {/*    >*/}
                  {/*      <option value="">Select a season...</option>*/}
                  {/*      {seasonList.map(season => (*/}
                  {/*          <option key={season.seasonId} value={season.seasonName}>{season.seasonName}</option>*/}
                  {/*      ))}*/}
                  {/*    </select>*/}
                  {/*  </Col>*/}
                  {/*</Row>*/}
                  <Row className="justify-content-center">
                    <Col md="3" xs="12">
                    </Col>
                    <Col md="6" xs="12">
                    </Col>
                    <Col md="3" xs="12">
                      <div className="text-center">
                      {(
                            (sessionStorage.getItem("curated-songs") && sessionStorage.getItem("curated-songs") != 'null' && JSON.parse(sessionStorage.getItem("curated-songs")).length > 0 && !sessionStorage.getItem("playlist")) ||
                            (sessionStorage.getItem("playlist") && sessionStorage.getItem("workingOnDraft") === "true")
                        ) && <>
                        <Button
                            className="btn-icon mb-3 mb-sm-0 mx-auto float-left"
                            color="primary"
                            href="/scripture/curated-songs"
                        >
                                  <span className="btn-inner--icon mr-1">
                                    <RiEditFill title="Edit" size="16"/>
                                  </span>
                            <span className="btn-inner--text">Go to song-flow</span>
                        </Button>
                      </>
                      }
                      </div>
                    </Col>
                  </Row>
                  
                  <Row className="justify-content-center">
                    <div className="text-center mt-1 mb-3">
                      <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                        {choosenDateMessage}
                      </div>
                    </div>
                  </Row>

                  <Row className="justify-content-center">
                  <Col lg="12">
                      <div className={styles.calendarWrapper}>
                        <DayPicker
                            mode="single"
                            selected={this.state.selectedDate ? new Date(this.state.selectedDate) : undefined}
                            onDayClick={this.handleDayClick}
                            month={this.state.month}
                            onMonthChange={this.handleMonthChange}
                            numberOfMonths={1}
                            modifiers={{
                              hasEntry: (date) => {
                                const dateStr = date.toLocaleDateString('en-GB', {
                                  timeZone: 'Europe/London',
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                }).split('/').reverse().join('-');

                                // Only highlight valid days with entries
                                const inMonth = date.getMonth() === this.state.month.getMonth();
                                const hasEntry = this.state.entries.some(e => e.date === dateStr);

                                return inMonth && hasEntry;
                              },
                              disabled: (date) => {
                                const dateStr = date.toLocaleDateString('en-GB', {
                                  timeZone: 'Europe/London',
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                }).split('/').reverse().join('-');

                                return !this.state.entries.some(e => e.date === dateStr);
                              }
                            }}

                            modifiersClassNames={{
                              hasEntry: 'has-entry',
                              today: 'today-highlight'
                            }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col lg="1"/>
                    <Col lg="5">
                      <Button
                          type="submit"
                          id="goToDateButton"
                          className="btn-icon mb-3 mb-sm-0 mt-5"
                          color="neutral"
                          onClick={() => this.setState({ month: new Date() })}
                      >
                        Go to Today
                      </Button>
                    </Col>
                    <Col lg="3"/>
                    <Col lg="3">
                      <div className="btn-wrapper">
                        <Button
                            type="submit"
                            id="continueButton"
                            className="btn-icon mb-3 mb-sm-0 mt-5"
                            color="primary"
                            onClick={() => {
                              // 1) fire the Recommended Event
                              if (typeof window.gtag === 'function') {
                                window.gtag('event', 'select_content', {
                                  content_type: 'lectionary'
                                });
                              }
                              // 2) then navigate
                              this.props.navigation('/lectionary/readings');
                            }}
                            disabled={this.isContinueButtonEnabled()}
                        >
                          <span className="btn-inner--text">Continue</span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Container>
          </section>
        </>
    );
  }
}

export default withNavigationHook(LectionaryDates);
